import moment from 'moment';
import { FC, ReactNode } from 'react';
import { FormattedCount } from 'components/formatted-count';
import styles from './field-grid.module.scss';

/** What an absent value renders as. The mock never leaves a field blank. */
export const EMPTY = '—';

export interface Field {
  label: string;
  value: ReactNode;
  /**
   * Rendered beside the label — the workspace's "Edited" pill, which marks the fields a
   * staged edit changed. Optional, so the Well page's own cards are unaffected.
   */
  labelSuffix?: ReactNode;
}

interface Props {
  fields: Field[];
}

// A count or depth for a card field — comma-separated, or an em-dash when absent. Depths
// run to five figures, so the separators are the difference between "19,860" and a
// number nobody can read at a glance.
export const fieldNumber = (value: number | null | undefined): JSX.Element => (
  <FormattedCount value={value} fallback={EMPTY} />
);

// A date for a card field, or an em-dash when absent or unparseable, so a bad timestamp
// reads as "no value" rather than "Invalid Date". Formatted through moment's `l`, as
// components/date does: `toLocaleDateString` follows the browser locale, not the app's.
export const fieldDate = (value: string | null | undefined): string => {
  if (!value) return EMPTY;
  const parsed = moment(value);

  return parsed.isValid() ? parsed.format('l') : EMPTY;
};

// Any text field, with the em-dash fallback applied consistently.
export const fieldText = (value: string | null | undefined): string => {
  return value?.trim() ? value : EMPTY;
};

/**
 * The label/value grid every data card uses, as a definition list so the pairing is
 * conveyed to assistive tech rather than implied by layout alone.
 * @param {Props} props The fields to render, in display order.
 * @return {JSX.Element} The grid.
 */
export const FieldGrid: FC<Props> = ({ fields }) => (
  <dl className={styles.grid}>
    {fields.map((field) => (
      <div className={styles.field} key={field.label}>
        <dt className={styles.label}>
          {field.label}
          {field.labelSuffix}
        </dt>
        <dd className={styles.value}>{field.value}</dd>
      </div>
    ))}
  </dl>
);
